/**
 * airlogi-bridge (Chrome 拡張) との通信ラッパー。
 *
 * 出荷チェックのエアロジ突合で使用 (2026-10-08)。Air-Logi は IP 制限 +
 * reCAPTCHA のためサーバから自動化できず、Kota さんの Chrome にログイン状態が
 * ある前提で拡張を経由してデータを取得する。
 * 実装はかんたん在庫 frontend/src/utils/airlogi-bridge.ts と同型。
 */

export type AirlogiAction =
  | "airlogi.getSessionStatus"
  | "airlogi.getStockInfo"
  | "airlogi.getIncomingStocks"
  | "airlogi.getUnallocatedOrders"
  | "airlogi.getAllocationHistory";

export interface AirlogiSuccess<D = unknown> {
  ok: true;
  data: D;
}

export interface AirlogiFailure {
  ok: false;
  error: string;
  message: string;
  details?: unknown;
}

export type AirlogiResponse<D = unknown> = AirlogiSuccess<D> | AirlogiFailure;

export interface SessionStatus {
  loggedIn: boolean;
  checkedAt: string;
}

export interface UnallocatedOrderRow {
  airlogiOrderId: string;
  orderNo: string;
  status: string;
  priority: string;
  shippingSchedule: string;
  registeredAt: string;
}

export interface AllocationHistoryRow {
  airlogiOrderId: string;
  orderNo: string;
  packingStatus: string;
  shippingStatus: string;
  trackingNo: string;
  orderDate: string;
  shippingSchedule: string;
  shippedDate: string;
  allocatedAt: string;
}

export interface StockInfoRow {
  jan: string;
  quantity: number;
  [key: string]: string | number | undefined;
}

export interface IncomingStockRow {
  expectedArrivalDate: string;
  jan: string;
  quantity: number;
  receivedQuantity: number;
  [key: string]: string | number | undefined;
}

// airlogi-bridge の拡張 ID (bridge リポジトリ docs/design.md 記載の固定 ID)
export const AIRLOGI_BRIDGE_EXTENSION_ID = "lmnlalkchcdmkoaigdbekeiacbpeogid";

const TIMEOUT_MS = 120_000;

type ChromeRuntime = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback?: (response: unknown) => void,
  ) => void;
  lastError?: { message?: string };
};

declare global {
  interface Window {
    chrome?: { runtime?: ChromeRuntime };
  }
}

export function callBridge<D = unknown>(
  action: AirlogiAction,
  params?: unknown,
): Promise<AirlogiResponse<D>> {
  const chrome = window.chrome?.runtime;
  if (!chrome || !chrome.sendMessage) {
    return Promise.resolve({
      ok: false,
      error: "EXTENSION_NOT_INSTALLED",
      message:
        "Chrome 拡張 airlogi-bridge にアクセスできません。" +
        "Chrome で拡張をインストール・有効化してからページを再読み込みしてください。",
    });
  }
  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      resolve({
        ok: false,
        error: "TIMEOUT",
        message: `拡張からの応答が ${TIMEOUT_MS / 1000} 秒以内に返りませんでした`,
      });
    }, TIMEOUT_MS);
    try {
      chrome.sendMessage(
        AIRLOGI_BRIDGE_EXTENSION_ID,
        { action, params, requestId: crypto.randomUUID() },
        (response: unknown) => {
          window.clearTimeout(timeoutId);
          if (chrome.lastError) {
            resolve({
              ok: false,
              error: "EXTENSION_NOT_INSTALLED",
              message:
                chrome.lastError.message ||
                "拡張との通信に失敗しました。拡張が有効か確認してください。",
            });
            return;
          }
          if (!response || typeof response !== "object") {
            resolve({
              ok: false,
              error: "INTERNAL_ERROR",
              message: "拡張から不正なレスポンスが返りました",
              details: response,
            });
            return;
          }
          resolve(response as AirlogiResponse<D>);
        },
      );
    } catch (e: unknown) {
      window.clearTimeout(timeoutId);
      resolve({
        ok: false,
        error: "INTERNAL_ERROR",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  });
}
